import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  TrendingUp, 
  Building2, 
  DollarSign,
  AlertTriangle,
  ChevronRight,
  TrendingDown
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

interface MobileDashboardHeroProps {
  monthlyRevenue: number;
  projectStats: { total: number; inProgress: number };
  alertsCount: number;
}

const getScoreColor = (score: number): string => {
  if (score >= 70) return 'text-emerald-500';
  if (score >= 55) return 'text-yellow-500';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-500';
};

const getScoreLabel = (score: number): string => {
  if (score >= 85) return 'Excelente';
  if (score >= 70) return 'Bom';
  if (score >= 55) return 'Regular';
  if (score >= 40) return 'Atenção';
  return 'Crítico';
};

export const MobileDashboardHero = ({ 
  monthlyRevenue, 
  projectStats,
  alertsCount
}: MobileDashboardHeroProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState<{ day: string; value: number }[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<'up' | 'down' | 'stable'>('stable');

  useEffect(() => {
    const calculateScore = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const endDate = new Date(currentYear, currentMonth, 0).toISOString().split('T')[0];

        // Get last 7 days for sparkline
        const last7Days = Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          return date.toISOString().split('T')[0];
        });

        const [receitasRes, despesasRes, tasksRes, propostasRes, projectsRes, last7DaysRes] = await Promise.all([
          supabase.from('financeiro_usuario').select('valor').eq('tipo', 'recebimento').gte('data', startDate).lte('data', endDate),
          supabase.from('financeiro_usuario').select('valor').eq('tipo', 'despesa').gte('data', startDate).lte('data', endDate),
          supabase.from('project_tasks').select('status').gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
          supabase.from('propostas').select('status').neq('status', 'rascunho'),
          supabase.from('projects').select('progress, status').in('status', ['em_andamento', 'planejamento']),
          supabase.from('financeiro_usuario').select('data, valor').eq('tipo', 'recebimento').gte('data', last7Days[0]).lte('data', last7Days[6])
        ]);

        // Process 7-day revenue data for sparkline
        const dailyRevenue = last7Days.map(day => {
          const dayRevenue = last7DaysRes.data
            ?.filter(r => r.data === day)
            .reduce((sum, r) => sum + Number(r.valor), 0) || 0;
          return { day: day.slice(-2), value: dayRevenue };
        });
        setRevenueData(dailyRevenue);

        // Calculate trend (compare last 3 days avg vs first 4 days avg)
        const firstHalfAvg = dailyRevenue.slice(0, 4).reduce((sum, d) => sum + d.value, 0) / 4;
        const lastHalfAvg = dailyRevenue.slice(4).reduce((sum, d) => sum + d.value, 0) / 3;
        if (lastHalfAvg > firstHalfAvg * 1.1) setRevenueTrend('up');
        else if (lastHalfAvg < firstHalfAvg * 0.9) setRevenueTrend('down');
        else setRevenueTrend('stable');

        const totalReceitas = receitasRes.data?.reduce((sum, r) => sum + Number(r.valor), 0) || 0;
        const totalDespesas = despesasRes.data?.reduce((sum, d) => sum + Number(d.valor), 0) || 0;
        const profitMargin = totalReceitas > 0 ? Math.min(100, Math.max(0, ((totalReceitas - totalDespesas) / totalReceitas) * 100)) : 50;

        const totalTasks = tasksRes.data?.length || 0;
        const completedTasks = tasksRes.data?.filter(t => t.status === 'concluída').length || 0;
        const taskVelocity = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 50;

        const totalPropostas = propostasRes.data?.length || 0;
        const aprovadasCount = propostasRes.data?.filter((p: any) => p.status === 'aprovada').length || 0;
        const proposalConversion = totalPropostas > 0 ? (aprovadasCount / totalPropostas) * 100 : 50;

        const avgProgress = projectsRes.data && projectsRes.data.length > 0
          ? projectsRes.data.reduce((sum, p) => sum + (p.progress || 0), 0) / projectsRes.data.length
          : 50;

        const finalScore = Math.round(
          (profitMargin * 0.30) + (taskVelocity * 0.25) + (proposalConversion * 0.25) + (avgProgress * 0.20)
        );

        setScore(Math.min(100, Math.max(0, finalScore)));
      } catch (error) {
        console.error('Error calculating health score:', error);
        setScore(50);
      } finally {
        setLoading(false);
      }
    };

    calculateScore();
  }, [user?.id]);

  const formatCurrency = (value: number) => {
    if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}k`;
    return `R$ ${value.toFixed(0)}`;
  };

  const getTrendColor = () => {
    if (revenueTrend === 'up') return 'hsl(var(--success))';
    if (revenueTrend === 'down') return 'hsl(var(--destructive))';
    return 'hsl(var(--primary))';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      className="bento-card p-4 active:scale-[0.97] transition-all duration-200 ease-out"
    >
      {/* Top Row: Score + Quick Stats */}
      <div className="flex items-center gap-4">
        {/* Mini Score Circle */}
        <div className="relative flex-shrink-0">
          <svg className="w-16 h-16 transform -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="26"
              stroke="currentColor"
              strokeWidth="6"
              fill="transparent"
              className="text-muted/30"
            />
            <motion.circle
              cx="32"
              cy="32"
              r="26"
              strokeWidth="6"
              fill="transparent"
              strokeLinecap="round"
              className={getScoreColor(score)}
              initial={{ strokeDasharray: '0 163' }}
              animate={{ strokeDasharray: `${(score / 100) * 163} 163` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-lg font-bold ${getScoreColor(score)}`}>
              {loading ? '--' : score}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="flex-1 grid grid-cols-2 gap-2">
          {/* Revenue with Sparkline */}
          <div 
            className="flex items-center gap-2 p-2.5 rounded-xl bg-success/10 active:scale-[0.97] transition-transform cursor-pointer"
            onClick={() => navigate('/financeiro')}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5 text-success" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Receita</p>
                {revenueTrend === 'up' && <TrendingUp className="h-3 w-3 text-success" />}
                {revenueTrend === 'down' && <TrendingDown className="h-3 w-3 text-destructive" />}
              </div>
              <p className="text-sm font-bold text-foreground">{formatCurrency(monthlyRevenue)}</p>
            </div>
            {/* Mini Sparkline */}
            <div className="w-12 h-8 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={getTrendColor()} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={getTrendColor()} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={getTrendColor()}
                    strokeWidth={1.5}
                    fill="url(#sparklineGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div 
            className="flex items-center gap-2 p-2.5 rounded-xl bg-info/10 active:scale-[0.97] transition-transform cursor-pointer"
            onClick={() => navigate('/projects')}
          >
            <Building2 className="h-4 w-4 text-info" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projetos</p>
              <p className="text-sm font-bold text-foreground">{projectStats.total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Status + Alerts */}
      <div className="flex items-center justify-between mt-3 pt-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <span className="text-xs text-muted-foreground">Saúde:</span>
          <Badge variant="secondary" className={`text-xs ${getScoreColor(score)}`}>
            {getScoreLabel(score)}
          </Badge>
        </div>
        
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 text-xs gap-1 px-2"
          onClick={() => navigate('/alertas')}
        >
          {alertsCount > 0 ? (
            <>
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              <span className="text-amber-600">{alertsCount} alerta{alertsCount > 1 ? 's' : ''}</span>
            </>
          ) : (
            <>
              <TrendingUp className="h-3 w-3 text-emerald-500" />
              <span className="text-emerald-600">Tudo ok</span>
            </>
          )}
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </motion.div>
  );
};